import React, { useEffect, useState } from "react";
import { Box, Container } from "@material-ui/core";
import { ErrorBox, VFlex, Loading } from "shared";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { SearchUserForm } from "./components/SearchUserForm";
import { CountryCode } from "utils/market";
import { useSearchParams } from "utils/hooks";
import { ALL_ROLES_VALUE, Category, ROLES, Role, useListUsers } from "./queries/useListUsers";
import { useHistory } from "react-router-dom";
import { join } from "utils/misc";
import { Area } from "types";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 25;

export const Users: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [page, setPage] = useState(0);

  const searchQuery = useSearchParams(
    {
      category: Category.q,
      search: "",
      role: ALL_ROLES_VALUE as Role,
      country_code: "" as CountryCode,
    },
    {
      category: Object.values(Category),
      role: ROLES.map(r => r.value),
      country_code: Object.values(CountryCode),
    }
  );

  const {
    data,
    error,
    isFetching,
    isIdle,
    isLoading,
    isLoadingError: isError,
    isSuccess,
  } = useListUsers({
    ...searchQuery,
    page: page + 1,
    limit: PAGE_SIZE,
  });
  const rows = data?.items || [];

  useEffect(() => {
    setPage(0);
  }, [searchQuery.category, searchQuery.search, searchQuery.role, searchQuery.country_code]);

  const columns: GridColDef[] = [
    { field: "id", headerName: t("users.id"), minWidth: 100 },
    { field: "roles", headerName: t("users.roles"), minWidth: 150 },
    { field: "state", headerName: t("users.state"), minWidth: 150 },
    { field: "uid", headerName: t("users.uid"), minWidth: 200, flex: 1 },
    {
      field: "name",
      headerName: t("users.name"),
      valueGetter: params => [params.row.first_name, params.row.last_name].join(" "),
      minWidth: 200,
      flex: 1,
    },
    { field: "phone_number", headerName: t("users.phone_number"), minWidth: 200, flex: 1 },
    { field: "email", headerName: t("users.email"), minWidth: 200, flex: 1 },
  ];

  const onRowClick = (params: GridRowParams) => {
    const isPatient = params.row.roles.includes("patient");
    const isTherapist = params.row.roles.includes("therapist");
    const profilePath = isPatient
      ? join(Area.Patients, params.row.id)
      : isTherapist
        ? join(Area.Therapists, params.row.id)
        : join(Area.Users, params.row.id);
    const search = new URL(window.location.href).search;

    history.push(profilePath, search);
  };

  return (
    <VFlex>
      <Container maxWidth="md">
        <Box mb={3} style={{ marginTop: "108px" }}>
          <SearchUserForm {...searchQuery} />
        </Box>
      </Container>

      {!isIdle && (
        <Box bgcolor="background.paper" padding={2} overflow="auto" data-testid="search-results">
          {isError && <ErrorBox error={error!} verbose centered dark />}
          {isLoading && <Loading />}
          {isSuccess && (
            <DataGrid
              columns={columns}
              rows={rows}
              rowCount={data?.metadata?.total_count || 0}
              paginationMode="server"
              pageSize={PAGE_SIZE}
              onPageChange={page => setPage(page)}
              onRowClick={onRowClick}
              loading={isFetching}
              disableSelectionOnClick
              autoHeight
            />
          )}
        </Box>
      )}
    </VFlex>
  );
};
